import { FieldMask, Timestamp } from "@bufbuild/protobuf";

import {
  ApplyCouponRequest,
  ApplyCouponResponse,
  RemoveSubscriptionCouponRequest,
  RemoveSubscriptionCouponResponse,
  ValidateCouponRequest,
  ValidateCouponResponse,
} from "@safetyculture/s12-apis-connect-web/s12/subscriptions/admin/v1/coupons_pb";
import {
  CancelSubscriptionRequest,
  CancelSubscriptionResponse,
  CreateBillingDetailsRequest,
  CreateBillingDetailsResponse,
  CreateEnterpriseMetadataRequest,
  CreateEnterpriseMetadataResponse,
  CreateTrialSubscriptionRequest,
  CreateTrialSubscriptionResponse,
  DeletePaymentMethodsRequest,
  DeletePaymentMethodsResponse,
  GetMetadataPlanTypeRequest,
  GetMetadataPlanTypeResponse,
  GetPaymentMethodsRequest,
  GetPaymentMethodsRequest_DeletionStatus,
  GetPaymentMethodsRequest_Filters,
  GetPaymentMethodsResponse,
  ReconcileSubscriptionRequest,
  ReconcileSubscriptionResponse,
  RetryPaymentCollectionRequest,
  RetryPaymentCollectionResponse,
  SyncPaymentMethodRequest,
  SyncPaymentMethodResponse,
  SyncWithZuoraRequest,
  SyncWithZuoraResponse,
  UpdateSubscriptionCurrencyRequest,
  UpdateSubscriptionCurrencyResponse,
  UpdateTaxExemptionRequest,
  UpdateTaxExemptionResponse,
} from "@safetyculture/s12-apis-connect-web/s12/subscriptions/admin/v1/subscriptions_pb";
import {
  Address,
  BillingDetails,
  Contact,
  ProductPlanPeriod,
} from "@safetyculture/s12-apis-connect-web/s12/subscriptions/v1/billing_pb";
import { ContractMetadata } from "@safetyculture/s12-apis-connect-web/s12/subscriptions/v1/contracts_metadata_pb";
import { BillingFrequency } from "@safetyculture/s12-apis-connect-web/s12/subscriptions/v1/contracts_pb";
import { PaymentMethodType } from "@safetyculture/s12-apis-connect-web/s12/subscriptions/v1/payment_method_pb";
import {
  ConvertTrialSubscriptionRequest,
  ConvertTrialSubscriptionResponse,
  DowngradeSubscriptionToFreeRequest,
  DowngradeSubscriptionToFreeResponse,
  GetBillingDetailsRequest,
  GetBillingDetailsResponse,
  ReactivateSubscriptionRequest,
  ReactivateSubscriptionResponse,
  UpdateBillingDetailsRequest,
  UpdateBillingDetailsResponse,
  UpdateCancelAtEndOfPeriodRequest,
  UpdateCancelAtEndOfPeriodResponse,
  UpgradeSubscriptionToPremiumRequest,
  UpgradeSubscriptionToPremiumResponse,
} from "@safetyculture/s12-apis-connect-web/s12/subscriptions/v1/subscriptions_pb";
import {
  ChargifyState,
  PaymentMethod,
  UpdatePaymentMethodRequest,
} from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_payment_pb";
import {
  PauseSalesforceSyncRequest,
  PauseSalesforceSyncResponse,
  UpdateProductPlanRequest,
  UpdateProductPlanResponse,
  UpdateSeatMovementsRestrictionRequest,
  UpdateTrialDateRequest,
} from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_pb";
import {
  GetSubscriptionSeatUsageRequest,
  GetSubscriptionSeatUsageResponse,
} from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_seats_pb";

import { supportServiceConnectClient } from "../index";
import { RequestMetadata } from "api/types";

type GetPlanTypeMetadataParams = {
  subId: string;
};

export const getPlanTypeMetadata = async (
  { subId }: GetPlanTypeMetadataParams,
  headers: Record<RequestMetadata, string>
): Promise<GetMetadataPlanTypeResponse> => {
  const req = new GetMetadataPlanTypeRequest({
    subscriptionId: subId,
  });

  return supportServiceConnectClient.getPlanTypeMetadata(req, { headers });
};

type SetEnterpriseMetadataParams = {
  subId: string;
};

export const setEnterpriseMetadata = async (
  { subId }: SetEnterpriseMetadataParams,
  headers: Record<RequestMetadata, string>
): Promise<CreateEnterpriseMetadataResponse> => {
  const req = new CreateEnterpriseMetadataRequest({
    subscriptionId: subId,
  });

  return supportServiceConnectClient.createEnterpriseMetadata(req, { headers });
};

type GetBillingDetailsParams = {
  orgId: string;
};

// Billing details

export const getBillingDetails = async (
  { orgId }: GetBillingDetailsParams,
  headers: Record<RequestMetadata, string>
): Promise<GetBillingDetailsResponse> => {
  const req = new GetBillingDetailsRequest({
    organizationId: orgId,
  });

  return supportServiceConnectClient.getBillingDetails(req, { headers });
};

type CreateBillingDetailsParams = {
  billingDetails: BillingDetails;
};

export const createBillingDetails = async (
  { billingDetails }: CreateBillingDetailsParams,
  headers: Record<RequestMetadata, string>
): Promise<CreateBillingDetailsResponse> => {
  const req = new CreateBillingDetailsRequest({
    billingDetails,
  });

  return supportServiceConnectClient.createBillingDetails(req, { headers });
};

type UpdateBillingDetailsParams = {
  orgId: string;
  billingDetails: BillingDetails;
};

export const updateBillingDetails = async (
  { orgId, billingDetails }: UpdateBillingDetailsParams,
  headers: Record<RequestMetadata, string>
): Promise<UpdateBillingDetailsResponse> => {
  const billingDetailsFieldMask = new FieldMask({
    paths: [
      "legal_name",
      "tax_number",
      "locale",
      "billing_contact",
      "shipping_contact",
      "billing_address",
      "shipping_address",
    ],
  });

  const req = new UpdateBillingDetailsRequest({
    organizationId: orgId,
    billingDetails,
    updateMask: billingDetailsFieldMask,
  });

  return supportServiceConnectClient.updateBillingDetails(req, { headers });
};

// Coupons

type ValidateCouponParams = {
  code: string;
  provider: string;
};

export const validateCoupon = async (
  { code, provider }: ValidateCouponParams,
  headers: Record<RequestMetadata, string>
): Promise<ValidateCouponResponse> => {
  const req = new ValidateCouponRequest({
    code,
    provider,
  });

  return supportServiceConnectClient.validateCoupon(req, { headers });
};

type ApplyCouponParams = {
  orgId: string;
  code: string;
};

export const applyCoupon = async (
  { orgId, code }: ApplyCouponParams,
  headers: Record<RequestMetadata, string>
): Promise<ApplyCouponResponse> => {
  const req = new ApplyCouponRequest({
    organisationId: orgId,
    code,
  });

  return supportServiceConnectClient.applyCoupon(req, { headers });
};

type RemoveCouponParams = {
  orgId: string;
  code: string;
};

export const removeCoupon = async (
  { orgId, code }: RemoveCouponParams,
  headers: Record<RequestMetadata, string>
): Promise<RemoveSubscriptionCouponResponse> => {
  const req = new RemoveSubscriptionCouponRequest({
    organisationId: orgId,
    code,
  });

  return supportServiceConnectClient.removeSubscriptionCoupon(req, { headers });
};

// Subscription

type UpdateTrialDateParams = {
  subscriptionId: string;
  organizationId: string;
  newTrialEndDate: Timestamp;
  endTrial?: boolean;
};

export const updateTrialDate = async (
  {
    organizationId,
    subscriptionId,
    newTrialEndDate,
    endTrial,
  }: UpdateTrialDateParams,
  headers: Record<RequestMetadata, string>
): Promise<Timestamp | undefined> => {
  const req = new UpdateTrialDateRequest({
    subscriptionId,
    organizationId,
    trialDate: newTrialEndDate,
    endTrial,
  });

  return supportServiceConnectClient
    .updateTrialDate(req, { headers })
    .then((res) => res.trialDate);
};

type CancelSubscriptionParams = {
  subscriptionId: string;
  message?: string;
};

export const cancelSubscription = async (
  { subscriptionId, message }: CancelSubscriptionParams,
  headers: Record<RequestMetadata, string>
): Promise<CancelSubscriptionResponse> => {
  const req = new CancelSubscriptionRequest({
    subscriptionId,
    message,
  });

  return supportServiceConnectClient.cancelSubscription(req, { headers });
};

export type UpdateCancelAtEndOfPeriodParams = {
  organizationId: string;
  subscriptionId: string;
  cancelAtEndOfPeriod: boolean;
  message?: string;
};

export const updateCancelAtEndOfPeriod = async (
  {
    organizationId,
    subscriptionId,
    cancelAtEndOfPeriod,
    message,
  }: UpdateCancelAtEndOfPeriodParams,
  headers: Record<RequestMetadata, string>
): Promise<UpdateCancelAtEndOfPeriodResponse> => {
  const req = new UpdateCancelAtEndOfPeriodRequest({
    organizationId,
    subscriptionId,
    cancelAtEndOfPeriod,
    message,
  });

  return supportServiceConnectClient.updateCancelAtEndOfPeriod(req, {
    headers,
  });
};

type UpdateSeatMovementsRestrictionParams = {
  restrictSeatMovements: boolean;
  organisationId: string;
};

export const updateSeatMovementsRestriction = async (
  {
    restrictSeatMovements,
    organisationId,
  }: UpdateSeatMovementsRestrictionParams,
  headers: Record<RequestMetadata, string>
): Promise<UpdateSeatMovementsRestrictionParams | undefined> => {
  const req = new UpdateSeatMovementsRestrictionRequest({
    restrictSeatMovements,
    organisationId,
  });

  return supportServiceConnectClient
    .updateSeatMovementsRestriction(req, {
      headers,
    })
    .then((res) => {
      return {
        restrictSeatMovements: res.restrictSeatMovements,
        organisationId: res.organisationId,
      };
    });
};

type UpdateSubscriptionCurrencyParams = {
  subscriptionId: string;
  currency: string;
  orgId: string;
};

export const updateSubscriptionCurrency = async (
  { subscriptionId, currency, orgId }: UpdateSubscriptionCurrencyParams,
  headers: Record<RequestMetadata, string>
): Promise<UpdateSubscriptionCurrencyResponse> => {
  const req = new UpdateSubscriptionCurrencyRequest({
    subscriptionId,
    currency,
    organisationId: orgId,
  });

  return supportServiceConnectClient.updateSubscriptionCurrency(req, {
    headers,
  });
};

type DowngradeSubscriptionToFreeParams = {
  orgId: string;
  userId: string;
  downgradeReason: string;
};

export const downgradeSubscriptionToFree = async (
  { orgId, userId, downgradeReason }: DowngradeSubscriptionToFreeParams,
  headers: Record<RequestMetadata, string>
): Promise<DowngradeSubscriptionToFreeResponse> => {
  const req = new DowngradeSubscriptionToFreeRequest({
    organizationId: orgId,
    userId,
    downgradeReason,
  });

  return supportServiceConnectClient.downgradeSubscriptionToFree(req, {
    headers,
  });
};

export type ReactivateSubscriptionParams = {
  orgId: string;
  userId: string;
  billingContact: Contact;
  shippingContact: Contact;
  couponCode?: string;
  shippingAddress: Address;
  billingAddress: Address;
  includeTrial: boolean;
  metadata?: ContractMetadata;
  billingFrequency?: BillingFrequency;
  // Deprecated fields - for backwards compatibility
  contactEmail?: string;
  contactNumber?: string;
};

export const reactivateSubscription = async (
  {
    orgId,
    userId,
    billingContact,
    shippingContact,
    couponCode,
    shippingAddress,
    billingAddress,
    includeTrial,
    metadata,
    billingFrequency,
    contactEmail,
    contactNumber,
  }: ReactivateSubscriptionParams,
  headers: Record<RequestMetadata, string>
): Promise<ReactivateSubscriptionResponse> => {
  const req = new ReactivateSubscriptionRequest({
    organizationId: orgId,
    userId: userId,
    billingContact,
    shippingContact,
    shippingAddress,
    billingAddress,
    includeTrial,
  });

  if (couponCode) {
    req.couponCode = couponCode;
  }

  if (metadata) {
    req.metadata = metadata;
  }

  if (billingFrequency) {
    req.billingFrequency = billingFrequency;
  }

  // Set deprecated fields for backwards compatibility if provided
  if (contactEmail) {
    req.contactEmail = contactEmail;
  }
  if (contactNumber) {
    req.contactNumber = contactNumber;
  }

  return supportServiceConnectClient.reactivateSubscription(req, { headers });
};

type UpgradeSubscriptionToPremiumParams = {
  orgId: string;
  userId: string;
  legalName: string;
  billingContact: Contact;
  shippingContact: Contact;
  billingAddress: Address;
  shippingAddress: Address;
  billingFrequency: BillingFrequency;
  currency: string;
  metadata?: ContractMetadata;
  isDecoupledTrial?: boolean;
  // Deprecated fields - for backwards compatibility
  contactEmail?: string;
  contactNumber?: string;
};

export const upgradeSubscriptionToPremium = async (
  {
    orgId,
    userId,
    legalName,
    billingContact,
    shippingContact,
    billingAddress,
    shippingAddress,
    billingFrequency,
    currency,
    metadata,
    isDecoupledTrial,
    contactEmail,
    contactNumber,
  }: UpgradeSubscriptionToPremiumParams,
  headers: Record<RequestMetadata, string>
): Promise<UpgradeSubscriptionToPremiumResponse> => {
  const req = new UpgradeSubscriptionToPremiumRequest({
    organizationId: orgId,
    userId,
    legalName,
    billingContact,
    shippingContact,
    billingAddress,
    shippingAddress,
    billingFrequency,
    currency,
  });

  if (metadata) {
    req.metadata = metadata;
  }

  if (isDecoupledTrial !== undefined) {
    req.isDecoupledTrial = isDecoupledTrial;
  }

  // Set deprecated fields for backwards compatibility if provided
  if (contactEmail) {
    req.contactEmail = contactEmail;
  }
  if (contactNumber) {
    req.contactNumber = contactNumber;
  }

  return supportServiceConnectClient.upgradeSubscriptionToPremium(req, {
    headers,
  });
};

type CreateTrialSubscriptionParams = {
  orgId: string;
  isDecoupledTrial: boolean;
  billingFrequency: BillingFrequency;
  legalName: string;
  billingContact: Contact;
  billingAddress: Address;
  shippingContact: Contact;
  shippingAddress: Address;
  metadata?: ContractMetadata;
  currency: string;
};

export const createTrialSubscription = async (
  {
    orgId,
    isDecoupledTrial,
    billingFrequency,
    legalName,
    billingContact,
    billingAddress,
    shippingContact,
    shippingAddress,
    metadata,
    currency,
  }: CreateTrialSubscriptionParams,
  headers: Record<RequestMetadata, string>
): Promise<CreateTrialSubscriptionResponse> => {
  const req = new CreateTrialSubscriptionRequest({
    organizationId: orgId,
    isDecoupledTrial,
    billingFrequency,
    legalName,
    billingContact,
    billingAddress,
    shippingContact,
    shippingAddress,
    currency,
  });

  if (metadata) {
    req.metadata = metadata;
  }

  return supportServiceConnectClient.createTrialSubscription(req, {
    headers,
  });
};

type UpdateProductPlanParams = {
  orgId: string;
  subscriptionId: string;
  product: ProductPlanPeriod;
};

export const updateProductPlan = async (
  { orgId, subscriptionId, product }: UpdateProductPlanParams,
  headers: Record<RequestMetadata, string>
): Promise<UpdateProductPlanResponse> => {
  const req = new UpdateProductPlanRequest({
    organizationId: orgId,
    subscriptionId,
    product,
  });

  return supportServiceConnectClient.updateProductPlan(req, {
    headers,
  });
};

type GetSubscriptionSeatUsage = {
  subscriptionId: string;
  orgId: string;
};

export const getSubscriptionSeatUsage = async (
  { subscriptionId, orgId }: GetSubscriptionSeatUsage,
  headers: Record<RequestMetadata, string>
): Promise<GetSubscriptionSeatUsageResponse> => {
  const req = new GetSubscriptionSeatUsageRequest({
    subscriptionId,
    roleId: orgId,
  });

  return supportServiceConnectClient.getSubscriptionSeatUsage(req, {
    headers,
  });
};

type UpdatePaymentMethodParams = {
  subscriptionId: string;
  paymentMethod: PaymentMethod;
  chargifyState: ChargifyState;
  organizationId: string;
};

export const updatePaymentMethod = async (
  {
    subscriptionId,
    paymentMethod,
    chargifyState,
    organizationId,
  }: UpdatePaymentMethodParams,
  headers: Record<RequestMetadata, string>
): Promise<PaymentMethod> => {
  const req = new UpdatePaymentMethodRequest({
    subscriptionId,
    paymentMethod,
    chargifyState,
    organizationId,
  });

  return supportServiceConnectClient
    .updatePaymentMethod(req, {
      headers,
    })
    .then((res) => res.paymentMethod);
};

type PauseSalesForceParams = {
  organizationId: string;
};

export const pauseSalesForceSyncMethod = async (
  { organizationId }: PauseSalesForceParams,
  headers: Record<RequestMetadata, string>
): Promise<PauseSalesforceSyncResponse> => {
  const req = new PauseSalesforceSyncRequest({
    organizationId,
  });

  return supportServiceConnectClient.pauseSalesforceSync(req, {
    headers,
  });
};

type UpdateTaxExemptionParams = {
  organizationId: string;
  isTaxExempt: boolean;
  exemptCertificateId?: string;
  exemptEntityUseCode?: string;
};

export const updateTaxExemption = async (
  {
    organizationId,
    isTaxExempt,
    exemptCertificateId,
    exemptEntityUseCode,
  }: UpdateTaxExemptionParams,
  headers: Record<RequestMetadata, string>
): Promise<UpdateTaxExemptionResponse> => {
  const req = new UpdateTaxExemptionRequest({
    organizationId,
    isTaxExempt,
    exemptCertificateId,
    exemptEntityUseCode,
  });

  return supportServiceConnectClient.updateTaxExemption(req, {
    headers,
  });
};

export enum DeletionStatus {
  UNSPECIFIED = 0,
  EXCLUDE_SOFT_DELETED = 1,
  SOFT_DELETED_ONLY = 2,
}

type GetPaymentMethodFilters = {
  deletionStatus?: DeletionStatus;
  paymentMethodType?: PaymentMethodType;
};

type GetPaymentMethodsParams = {
  organizationId: string;
  filters?: GetPaymentMethodFilters;
};

export const getPaymentMethods = async (
  { organizationId, filters }: GetPaymentMethodsParams,
  headers: Record<RequestMetadata, string>
): Promise<GetPaymentMethodsResponse> => {
  const req = new GetPaymentMethodsRequest({
    organizationId,
  });

  if (filters) {
    const deletionStatusMap = {
      [DeletionStatus.UNSPECIFIED]:
        GetPaymentMethodsRequest_DeletionStatus.UNSPECIFIED,
      [DeletionStatus.EXCLUDE_SOFT_DELETED]:
        GetPaymentMethodsRequest_DeletionStatus.EXCLUDE_SOFT_DELETED,
      [DeletionStatus.SOFT_DELETED_ONLY]:
        GetPaymentMethodsRequest_DeletionStatus.SOFT_DELETED_ONLY,
    };

    const getFilter = new GetPaymentMethodsRequest_Filters();
    if (filters.deletionStatus) {
      getFilter.deletionStatus = deletionStatusMap[filters.deletionStatus];
    }

    if (filters.paymentMethodType) {
      getFilter.paymentMethodType = filters.paymentMethodType;
    }

    req.filters = getFilter;
  }

  return supportServiceConnectClient.getPaymentMethods(req, { headers });
};

type DeletePaymentMethodsParams = {
  organizationId: string;
  ids: string[];
};

export const deletePaymentMethods = async (
  { organizationId, ids }: DeletePaymentMethodsParams,
  headers: Record<RequestMetadata, string>
): Promise<DeletePaymentMethodsResponse> => {
  const req = new DeletePaymentMethodsRequest({
    organizationId,
    ids,
  });

  return supportServiceConnectClient.deletePaymentMethods(req, { headers });
};

export const syncPaymentMethod = async (
  { organizationId }: { organizationId: string },
  headers: Record<RequestMetadata, string>
): Promise<SyncPaymentMethodResponse> => {
  const req = new SyncPaymentMethodRequest({
    organizationId,
  });

  return supportServiceConnectClient.syncPaymentMethod(req, { headers });
};

type RetryPaymentParams = {
  subscriptionId: string;
};

export const retryPayment = async (
  { subscriptionId }: RetryPaymentParams,
  headers: Record<RequestMetadata, string>
): Promise<RetryPaymentCollectionResponse> => {
  const req = new RetryPaymentCollectionRequest({
    subscriptionId,
  });

  return supportServiceConnectClient.retryPaymentCollection(req, { headers });
};

type ConvertTrialParams = {
  organizationId: string;
  userId: string;
  couponCode?: string;
};

export const convertTrial = async (
  { organizationId, userId, couponCode }: ConvertTrialParams,
  headers: Record<RequestMetadata, string>
): Promise<ConvertTrialSubscriptionResponse> => {
  const req = new ConvertTrialSubscriptionRequest({
    organizationId,
    userId,
    couponCode,
  });

  return supportServiceConnectClient.convertTrialSubscription(req, { headers });
};

type SyncZuoraDataParams = {
  subscriptionId: string;
  zuoraAccountNumber?: string;
  zuoraSubscriptionNumber?: string;
  zuoraAccountId?: string;
  zuoraSubscriptionId?: string;
};

export const syncWithZuora = async (
  {
    subscriptionId,
    zuoraAccountNumber,
    zuoraSubscriptionNumber,
    zuoraAccountId,
    zuoraSubscriptionId,
  }: SyncZuoraDataParams,
  headers: Record<RequestMetadata, string>
): Promise<SyncWithZuoraResponse> => {
  const req = new SyncWithZuoraRequest({
    subscriptionId,
    zuoraAccountNumber: zuoraAccountNumber || "",
    zuoraSubscriptionNumber: zuoraSubscriptionNumber || "",
    zuoraAccountId: zuoraAccountId || "",
    zuoraSubscriptionId: zuoraSubscriptionId || "",
  });

  return supportServiceConnectClient.syncWithZuora(req, { headers });
};

type ReconcileSubscriptionParams = {
  organizationId: string;
};

export const reconcileSubscriptionMethod = async (
  { organizationId }: ReconcileSubscriptionParams,
  headers: Record<RequestMetadata, string>
): Promise<ReconcileSubscriptionResponse> => {
  const req = new ReconcileSubscriptionRequest({ organizationId });
  return supportServiceConnectClient.reconcileSubscription(req, { headers });
};
